import {isEmpty, orderBy} from 'lodash';
import {Injectable} from '@angular/core';
import {ArrayUtil} from '../../utils/array.util';
import {PortfolioType} from '../../model/portfolio-type.model';
import {PortfolioService} from '../../service/portfolio.service';
import {Keys, StorageService} from '../../service/storage.service';
import {GaugedInvestment} from '../../model/gauged-investment.model';
import {FullPageLoader} from '../../full-page-loader/full-page-loader';
import {SelectedInvestments} from '../../model/selected-investments.model';
import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from '@angular/router';
import {ManualInvestPortfolioCreationData} from '../../model/portfolio-creation-data/manual-invest-portfolio-creation-data.model';

@Injectable()
export class ManualInvestListedLoansResolver implements Resolve<{ potentialInvestments: GaugedInvestment[], selectedInvestments: SelectedInvestments }> {
    private readonly storageService: StorageService;
    private readonly fullPageLoader: FullPageLoader;
    private readonly portfolioService: PortfolioService;

    constructor(portfolioService: PortfolioService, storageService: StorageService, fullPageLoader: FullPageLoader) {
        this.fullPageLoader = fullPageLoader;
        this.storageService = storageService;
        this.portfolioService = portfolioService;
    }

    public async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<{ potentialInvestments: GaugedInvestment[], selectedInvestments: SelectedInvestments }> {
        this.fullPageLoader.showLoader();
        try {
            const tempPortfolioId = route.params.tempPortfolioId;
            const portfolioCreationData = await this.portfolioService.getLocalPortfolioCreationData(tempPortfolioId) as ManualInvestPortfolioCreationData;
            const portfolioAmount = portfolioCreationData.portfolioAmount;
            const potentialInvestments = await this.getPotentialInvestments(portfolioAmount);
            const selectedInvestments = await this.getSelectedInvestmentsWhichAreStillAvailable(potentialInvestments, portfolioCreationData);
            return {
                potentialInvestments: potentialInvestments,
                selectedInvestments: selectedInvestments
            };
        } finally {
            this.fullPageLoader.hideLoader();
        }
    }

    private async getSelectedInvestmentsWhichAreStillAvailable(potentialInvestments: GaugedInvestment[], portfolioCreationData: ManualInvestPortfolioCreationData): Promise<SelectedInvestments> {
        const selectedInvestments = portfolioCreationData.selectedInvestments ? portfolioCreationData.selectedInvestments : SelectedInvestments.empty();

        const filteredSelectedInvestments = selectedInvestments.filter(selectedInvestment => {
            const potentialLoanIdsForInvestment = potentialInvestments.map(potentialInvestment => potentialInvestment.loan._id);
            return potentialLoanIdsForInvestment.includes(selectedInvestment.gaugedInvestment.loan._id);
        }).filter(selectedInvestment => {
            const maximumAmountAvailableToInvest = potentialInvestments.find(potentialInvestment => potentialInvestment.loan._id === selectedInvestment.gaugedInvestment.loan._id).amount;
            return selectedInvestment.amount <= maximumAmountAvailableToInvest;
        });

        if (!isEmpty(selectedInvestments)) {
            ArrayUtil.clear(selectedInvestments);
            await this.portfolioService.createOrUpdateLocalPortfolioCreationData(portfolioCreationData);
        }
        return SelectedInvestments.from(filteredSelectedInvestments);
    }

    private async getPotentialInvestments(portfolioAmount: number): Promise<GaugedInvestment[]> {
        const lenderId = this.storageService.getItem(Keys.LENDER_ID);
        const gaugedInvestments = await this.portfolioService.getPotentialInvestments(portfolioAmount, lenderId, PortfolioType.ManualInvest);
        return orderBy(gaugedInvestments, [
            (gaugedInvestment: GaugedInvestment) => gaugedInvestment.loan.filled_percentage,
            (gaugedInvestment: GaugedInvestment) => gaugedInvestment.loan.loan_accepted_on
        ], ['desc', 'asc']);
    }
}